import moment from 'moment'
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import { CustomPopup } from '../../../common/Popup/CustomPopup'
import MiniPopup from '../../../common/Popup/MiniPopup'
import {
	AUTOCOMPLETE_TYPE,
	DETAILS_APPLY_FOR,
	WEIGHT_UNIT_SELECT_ITEMS,
	SHIPPING_TYPE,
	FORM_FIELDS,
	SCC_CODES,
	PHARMA_SHIPPING_TYPE,
	PHARMA_PRODUCT,
	FRESH_FORWARD_PRODUCT,
	getInitPharmaObject,
	emailPattern,
} from '../../../constants'
import {
	calculateDimensionToCm,
	calculateWeightToKg,
	convertToLowerCase,
} from '../../../utils/common'
import {
	getFromSessionStorage,
} from '../../../utils/StorageUtils'
import getCurrentUser from '../../../utils/getCurrentUser'
import getPrincipal from '../../../utils/getPrincipal'
import { PrimaryButton, SecondaryButton } from '../../../common/Buttons'
import deleteIcon from '../../../assets/images/delete_icon_red.png'
import plusIcon from '../../../assets/images/plusBlue.svg'
import CommonInput from '../../../common/Resusable/CommonInput'
import { CommonDropDown } from '../../../common/Resusable/CommonDropDown'
import { ALLOWABLE_BOOKING_DATE } from '../../../configs/appConfigs'
import AirportAutocomplete from '../../AirportAutocomplete'
import { CustomToast } from '../../../common/CustomToast/CustomToast'
import AppCalendar from '../../../common/Calendar'
import ShippingItemAutocomplete from '../../ShippingItemAutocomplete'
import SccAutoComplete from '../../SccAutoComplete'
import SccListDisplay from '../../SccListDisplay'
import LoadingIndicator from '../../LoadingIndicator'
import RadioToggle from '../../SearchPanel/RadioToggle'
import { AlertMessage } from '../../SearchPanel/AlertMessage'
import { submitSlrBooking, submitSlrCargoStack, classifyShipment, getSlrBookingDetailsResponse, saveSlrAttachment } from '../../../services/slrService'
import { slrBookingDetailsSuccess, slrBookingDetailsError, setSlrBookingResult } from '../../../redux/actions/slrBookingDetailsActions'
import MESSAGES from '../../../constants/Messages'
import {
	getShippingItems,
	checkSccCompatibility,
	getAirportsByCode,
} from '../../../services/searchPanelServices'
import { getStationByAgentCode } from '../../../services/dashboardServices'
import { validatAgentCodePattern } from '../../GsaUserAgentSelect/GsaUserAgentSelectUtil'
import { sendSlrNotificationEmails, getSlrFallbackStation } from '../SLRUtils'
import './SlrBooking.scss'
import './SlrBookingInputFields.scss'
import SlrPackagingRow from './SlrPackagingRow'
import SlrAdditionalDetailsSection from './Sections/SlrAdditionalDetailsSection'
import SlrSubmittedBySection from './Sections/SlrSubmittedBySection'
import SlrSummarySection from './Sections/SlrSummarySection'
import SlrInstructionsSection from './Sections/SlrInstructionsSection'

const createEmptyRow = (overrides = {}) => ({
	pieces: '',
	length: '',
	width: '',
	height: '',
	dimensionUnit: 'C',
	weight: '',
	weightUnit: 'K',
	weightType: DETAILS_APPLY_FOR.PER_ITEM,
	stackable: true,
	turnable: false,
	craneLiftable: null,
	forkLiftable: null,
	fragile: false,
	packingType: 'Select',
	...overrides,
})

const SERVICE_SCC_CODES = ['XPS']

const toNumber = (value) => Number(value) || 0

const hasCargoValuesChanged = (initial, current) => {
	if (!initial) return true
	if (toNumber(initial.totalWeight) !== toNumber(current.totalWeight)) return true
	if ((initial.weightUnit || 'K') !== (current.weightUnit || 'K')) return true
	if (initial.rows.length !== current.rows.length) return true
	return initial.rows.some((initialRow, index) => {
		const row = current.rows[index]
		return ['pieces', 'length', 'width', 'height', 'weight'].some(
			(key) => toNumber(initialRow[key]) !== toNumber(row[key])
		) ||
			(initialRow.dimensionUnit || 'C') !== (row.dimensionUnit || 'C') ||
			(initialRow.weightUnit || 'K') !== (row.weightUnit || 'K') ||
			initialRow.weightType !== row.weightType ||
			(initialRow.stackable === false) !== (row.stackable === false) ||
			(initialRow.turnable === true) !== (row.turnable === true)
	})
}

const toBooleanFromFlag = (value) => {
	if (value === true || value === 'Y') return true
	if (value === false || value === 'N') return false
	return null
}

const parsePrefillShippingDate = (value) => {
	if (!value) return null

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value
	}

	if (moment.isMoment(value)) {
		return value.isValid() ? value.toDate() : null
	}

	const normalized = String(value).trim()
	const nativeDate = new Date(normalized)
	if (!Number.isNaN(nativeDate.getTime())) {
		return nativeDate
	}

	const parsedDate = moment(
		normalized,
		[
			'DD-MMM-YYYY',
			'D-MMM-YYYY',
			'DD MMM, YYYY',
			'DD MMM YYYY',
			'D MMM YYYY',
			'YYYY-MM-DD',
			moment.ISO_8601,
		],
		'en',
		true
	)

	return parsedDate.isValid() ? parsedDate.toDate() : null
}

const SlrBookingInputFields = ({
	gsaAgentDetails,
	isEditMode = false,
	renderEditDetails,
}) => {
	const { t } = useTranslation(['labels', 'errors'])
	const history = useHistory()
	const location = useLocation()
	const { slrNumber } = useParams()
	const dispatch = useDispatch()
	const { slrBookingDetails: slrBookingData = {} } = useSelector(state => state.slrBookingDetails)
	const editSlrNumber = slrNumber || location?.state?.slrNumber
	const agentDetails = gsaAgentDetails?.agentDetails
	const [userDetails, setUserDetails] = useState(null)
	const [selectedOrigin, setSelectedOrigin] = useState(null)
	const [defaultOrigin, setDefaultOrigin] = useState(null)
	const [selectedDestination, setSelectedDestination] = useState(null)
	const [shippingDate, setShippingDate] = useState(null)
	const [shippingErrors, setShippingErrors] = useState({})

	const [shippingItems, setShippingItems] = useState([])
	const [shippingItem, setShippingItem] = useState(null)
	const [additionalSccs, setAdditionalSccs] = useState([])
	const [appliedSccs, setAppliedSccs] = useState([])
	const [appliedServices, setAppliedServices] = useState([])
	const [sccCodeEntered, setSccCodeEntered] = useState(null)
	const [autoAdd, setAutoAdd] = useState(false)
	const [warning, setWarning] = useState(false)
	const [additionalInfoData, setAdditionalInfoData] = useState({
		type: null,
		pharma: getInitPharmaObject(),
	})
	const [totalWeight, setTotalWeight] = useState('')
	const [weightUnit, setWeightUnit] = useState('K')
	const [cargoErrors, setCargoErrors] = useState({})
	const [sccWarning, setSccWarning] = useState(null)
	const [sccCompatibilityError, setSccCompatibilityError] = useState(null)
	const [sccCombinationError, setSccCombinationError] = useState(null)
	const [isStackable, setIsStackable] = useState(null)
	const [isTiltable, setIsTiltable] = useState(null)
	const [isCraneLiftable, setIsCraneLiftable] = useState(null)
	const [isForkLiftable, setIsForkLiftable] = useState(null)
	const [isFragile, setIsFragile] = useState(null)
	const [packagingRows, setPackagingRows] = useState([createEmptyRow()])
	const [packagingRowsErrors, setPackagingRowsErrors] = useState([])
	const [hasEditedPackaging, setHasEditedPackaging] = useState(false)
	const [shippingDescription, setShippingDescription] = useState('')
	const [hasAttachments, setHasAttachments] = useState(false)
	const [attachments, setAttachments] = useState([])
	const [attachmentError, setAttachmentError] = useState('')
	const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
	const attachmentUploadRef = useRef(false)
	const [airwayBillNumber, setAirwayBillNumber] = useState('')
	const [agentCodeInput, setAgentCode] = useState('')
	const agentCode = String(agentCodeInput || '').trim()
	const [validatedAgentDetails, setValidatedAgentDetails] = useState(null)
	const [showSubmittedBy, setShowSubmittedBy] = useState(false)
	const [submittedByErrors, setSubmittedByErrors] = useState({})
	const [isValidatingAgent, setIsValidatingAgent] = useState(false)
	const lastValidatedAgentCodeRef = useRef('')
	const agentValidationRequestRef = useRef(0)
	const [additionalPropsError, setAdditionalPropsError] = useState('')
	const [notification, setNotification] = useState(null)
	const notifMsgRef = useRef(null)
	const editPrefillAppliedRef = useRef(false)
	const initialCargoValuesRef = useRef(null)
	const initialAgentDetailsRef = useRef(null)
	const prefillSccCodesRef = useRef([])
	const skipShippingItemResetRef = useRef(false)
	const shippingItemPrefillAppliedRef = useRef(false)

	const [cargoRouteRows, setCargoRouteRows] = useState([])
	const [routeRowErrors, setRouteRowErrors] = useState({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showResultPopup, setShowResultPopup] = useState(false)
	const [resultData, setResultData] = useState(null)
	const [classificationData, setClassificationData] = useState(null)
	const [classificationAlertData, setClassificationAlertData] = useState(null)
	const [showNonSlrAlert, setShowNonSlrAlert] = useState(false)
	const [processErrorMessage, setProcessErrorMessage] = useState(null)


	/**
	 * Sets an error notification
	 * @param {string} errorMessage - The error message to display (can be i18n key)
	 */
	const setError = (errorMessage) => {
		setNotification({
			severity: 'error',
			summary: errorMessage,
		})
	}

	const handleSetError = (key, text) => {
		const errorKey =
			key === FORM_FIELDS.ORIGIN
				? 'origin'
				: key === FORM_FIELDS.DESTINATION
					? 'destination'
					: key
		setShippingErrors((prev) => ({ ...prev, [errorKey]: text }))
	}

	const clearError = (key) => {
		const errorKey =
			key === FORM_FIELDS.ORIGIN
				? 'origin'
				: key === FORM_FIELDS.DESTINATION
					? 'destination'
					: key
		setShippingErrors((prev) => ({ ...prev, [errorKey]: null }))
	}

	const clearCargoError = (key) => {
		setCargoErrors((prev) => ({ ...prev, [key]: null }))
	}

	const handleSccChange = (e) => {
		setSccCodeEntered(e?.target?.value)
	}

	const showCombinationError = (pharmaType, selectedScc) => {
		const convertedPharmaType = convertToLowerCase(pharmaType)
		setSccCombinationError(
			t(MESSAGES.COMPATIBILITY_ERROR, {
				SCC1: selectedScc,
				SCC2: convertedPharmaType,
				ns: 'errors',
			})
		)
	}

	const showSccCompatibilityError = (errorMsg) => {
		setSccCompatibilityError(errorMsg)
	}

	const showWarning = () => {
		setWarning(true)
	}

	const checkShippingTypeCombinationScc = (scc) => {
		let validCombination = true
		if (
			additionalInfoData?.pharma?.shippingPharmaType ===
			PHARMA_SHIPPING_TYPE.PASSIVE
		) {
			if (
				scc === SCC_CODES.PIP ||
				scc === SCC_CODES.ACT ||
				scc === SCC_CODES.FRO
			) {
				validCombination = false
			}
		}
		if (
			additionalInfoData?.pharma?.shippingPharmaType ===
			PHARMA_SHIPPING_TYPE.ACTIVE
		) {
			if (scc === SCC_CODES.PIP) {
				validCombination = false
			}
		}

		if (
			additionalInfoData?.pharma?.shippingPharmaType ===
			PHARMA_SHIPPING_TYPE.HYBRID
		) {
			if (scc === SCC_CODES.ACT) {
				validCombination = false
			}
		}
		return validCombination
	}

	const checkDryIceCombinationScc = (scc) => {
		let validCombination = true
		if (
			additionalInfoData?.pharma?.isDryIceAdded === 'NO' &&
			scc === SCC_CODES.ICE
		) {
			validCombination = false
		}
		return validCombination
	}

	const checkTemperatureCombinationScc = (scc) => {
		let validCombination = true
		const temperatureArray = [
			SCC_CODES.TMP1,
			SCC_CODES.TMP2,
			SCC_CODES.TMP3,
			SCC_CODES.FRO,
		]
		if (temperatureArray.includes(scc)) {
			temperatureArray.forEach((existingTempScc) => {
				if (
					scc !== existingTempScc &&
					(additionalSccs.map((item) => item.scc).includes(existingTempScc) ||
						appliedSccs.map((item) => item.scc).includes(existingTempScc))
				) {
					validCombination = false
				}
			})
		}
		return validCombination
	}

	const handleSccCompatibilityCheck = async (sccList) => {
		try {
			const responseBody = await checkSccCompatibility(sccList)

			if (responseBody?.compatible === false) {
				let message = responseBody?.message
				if (message) {
					message = message.replace('ER023', t('ER023', { ns: 'errors' }))
				}
				showSccCompatibilityError(message)
				return false
			}
			setSccCompatibilityError(null)
			return true
		} catch (error) {
			const errMsg = 'SCC compatibility check error: ' + (error?.message || '')
			showSccCompatibilityError(errMsg)
			return false
		}
	}

	const handleAddScc = async (sccData) => {
		if (!sccData) return

		const shipmentSccs = shippingItem?.scc
			? [
				{
					scc: shippingItem.scc,
					sccDescription: shippingItem.sccDescription || shippingItem.scc,
				},
			]
			: []
		const selectedSccList = [
			...shipmentSccs,
			...appliedSccs,
			...additionalSccs,
			...appliedServices,
		].map((item) => item.scc)

		const serviceSccCodes = ['XPS']
		if (serviceSccCodes.includes(sccData.scc)) {
			if (selectedSccList.includes(sccData.scc)) {
				if (!autoAdd) {
					setSccWarning(t(MESSAGES.SELECTED, { ns: 'errors' }))
				}
				showWarning()
				return
			}

			const serviceCompatible = await handleSccCompatibilityCheck({
				selectedItems: selectedSccList,
				newItem: sccData.scc,
			})

			if (serviceCompatible) {
				setAppliedServices([...appliedServices, sccData])
				setSccWarning(null)
				clearCargoError('scc')
				showWarning()
			}
			return
		}

		const tempSccCodes = [SCC_CODES.TMP1, SCC_CODES.TMP2, SCC_CODES.TMP3]

		if (!selectedSccList?.length && sccData?.scc) {
			setAdditionalSccs([...additionalSccs, sccData])
			if (tempSccCodes.includes(sccData.scc)) {
				setAppliedSccs([...appliedSccs, sccData])
			}
			setSccWarning(null)
			clearCargoError('scc')
			return
		}

		if (selectedSccList?.length && !selectedSccList?.includes(sccData?.scc)) {
			const isCompatible = await handleSccCompatibilityCheck({
				selectedItems: selectedSccList,
				newItem: sccData.scc,
			})

			if (additionalInfoData?.pharma?.shippingPharmaType && isCompatible) {
				if (
					!checkShippingTypeCombinationScc(sccData.scc) ||
					!checkDryIceCombinationScc(sccData.scc) ||
					!checkTemperatureCombinationScc(sccData.scc)
				) {
					showCombinationError(
						additionalInfoData.pharma.shippingPharmaType,
						sccData.scc
					)
				} else {
					setAdditionalSccs([...additionalSccs, sccData])
					if (tempSccCodes.includes(sccData.scc)) {
						setAppliedSccs([...appliedSccs, sccData])
					}
					if (
						sccData.scc === SCC_CODES.ICE &&
						additionalInfoData?.pharma?.tempratureRange
					) {
						setAdditionalInfoData({
							...additionalInfoData,
							pharma: { ...additionalInfoData.pharma, isDryIceAdded: 'YES' },
						})
					}
					if (
						sccData.scc === SCC_CODES.ACT &&
						(shippingItem?.product === FRESH_FORWARD_PRODUCT ||
							shippingItem?.product === PHARMA_PRODUCT)
					) {
						setAdditionalInfoData({
							...additionalInfoData,
							pharma: {
								...additionalInfoData.pharma,
								shippingPharmaType: PHARMA_SHIPPING_TYPE.ACTIVE,
							},
						})
					}
				}
			} else if (isCompatible) {
				setAdditionalSccs([...additionalSccs, sccData])
				if (tempSccCodes.includes(sccData.scc)) {
					setAppliedSccs([...appliedSccs, sccData])
				}
			}
			setSccWarning(null)
			clearCargoError('scc')
		} else if (autoAdd) {
			setSccWarning(null)
		} else {
			setSccWarning(t(MESSAGES.SELECTED, { ns: 'errors' }))
		}
	}

	const handleRemoveScc = (scc) => {
		if (additionalSccs.map((item) => item.scc).includes(scc.scc)) {
			setAdditionalSccs(additionalSccs.filter((item) => item.scc !== scc.scc))
		}
		if (appliedServices.map((item) => item.scc).includes(scc.scc)) {
			setAppliedServices(appliedServices.filter((item) => item.scc !== scc.scc))
		}
		// Clear errors when removing SCC
		setSccWarning(null)
		setSccCompatibilityError(null)
		clearCargoError('scc')
	}

	const handleTotalWeightChange = (val) => {
		const value = val.replace(/[^0-9.]/g, '')
		if (
			value.length <= 9 &&
			(!value.split('.')[1] || value.split('.')[1].length < 2) &&
			value.split('.').length <= 2
		) {
			setTotalWeight(value)
			clearCargoError('totalWeight')
		}
	}

	const setNotificationMessage = (msg) => {
		notifMsgRef.current = msg
	}

	const setNotificationType = (type) => {
		setNotification({
			severity: type?.toLowerCase() || 'error',
			summary: notifMsgRef.current,
		})
		notifMsgRef.current = null
	}

	const sccErrorMessage = sccCombinationError || sccCompatibilityError

	const handleCloseSccError = () => {
		if (sccCombinationError) {
			setSccCombinationError(null)
			return
		}
		setSccCompatibilityError(null)
	}

	const addRouteRow = () => {
		setCargoRouteRows((prev) => [
			...prev,
			{
				id: Date.now() + Math.floor(Math.random() * 1000),
				airport: null,
				carrier: '',
			},
		])
	}

	const removeRouteRow = (id) => {
		setCargoRouteRows((prev) => prev.filter((row) => row.id !== id))
		setRouteRowErrors((prev) => {
			const updated = { ...prev }
			delete updated[id]
			return updated
		})
	}

	const updateRouteRow = (id, field, value) => {
		setCargoRouteRows((prev) =>
			prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
		)
		setRouteRowErrors((prev) => {
			if (!prev[id]?.[field]) return prev
			const updated = { ...prev, [id]: { ...prev[id] } }
			delete updated[id][field]
			if (Object.keys(updated[id]).length === 0) delete updated[id]
			return updated
		})
	}

	const resolveShippingItemFromPrefill = (items, prefill, rawDetails) => {
		if (!Array.isArray(items) || items.length === 0) return null

		const commodityCode = prefill?.cargoDetails?.commodity
		const productSystemName = prefill?.cargoDetails?.commodity
		const productDisplayName = prefill?.cargoDetails?.product

		return (
			items.find(
				(item) =>
					commodityCode &&
					String(item?.code || '').toLowerCase() ===
					String(commodityCode).toLowerCase()
			) ||
			items.find(
				(item) =>
					productSystemName &&
					(String(item?.product || '').toLowerCase() ===
						String(productSystemName).toLowerCase() ||
						String(item?.serviceProduct || '').toLowerCase() ===
						String(productSystemName).toLowerCase())
			) ||
			items.find(
				(item) =>
					productDisplayName &&
					String(item?.description || '').toLowerCase() ===
					String(productDisplayName).toLowerCase()
			) ||
			null
		)
	}

	const applyPrefilledSccState = (shippingItemMatch = null) => {
		const prefilledSccs = Array.isArray(prefillSccCodesRef.current)
			? prefillSccCodesRef.current
			: [prefillSccCodesRef.current]
		const allSccCodes = prefilledSccs.reduce((sccList, item) => {
			const rawCode = typeof item === 'string' ? item : item?.scc
			if (typeof rawCode !== 'string') return sccList
			rawCode.split(',').forEach((value) => {
				const scc = value.trim().toUpperCase()
				if (scc && !sccList.some(existing => existing.scc === scc)) {
					sccList.push({
						...(typeof item === 'object' ? item : {}),
						scc,
						sccDescription: rawCode.includes(',') ? scc : item?.sccDescription || scc,
					})
				}
			})
			return sccList
		}, [])
		prefillSccCodesRef.current = allSccCodes

		const serviceSccs = allSccCodes.filter((code) =>
			SERVICE_SCC_CODES.includes(code.scc)
		)
		const nonServiceSccs = allSccCodes.filter(
			(code) => !SERVICE_SCC_CODES.includes(code.scc)
		)

		const shippingItemScc = shippingItemMatch?.scc?.trim().toUpperCase() || null
		const additionalSccList = nonServiceSccs.filter(
			(code) => !shippingItemScc || code.scc !== shippingItemScc
		)

		setAppliedServices(serviceSccs)
		setAdditionalSccs(additionalSccList)
	}

	const mapLooseDetailsToRows = (
		looseDetails = [],
		defaultWeightUnit = 'K'
	) => {
		if (!Array.isArray(looseDetails) || looseDetails.length === 0) {
			return [createEmptyRow()]
		}

		return looseDetails.map((item) => {
			const pieces = parseFloat(item?.numberOfPieces || item?.pieces) || 0
			const totalWeight = parseFloat(item?.weight || item?.weightPerPiece) || 0
			const derivedWeightType = item?.weightPerPiece ? DETAILS_APPLY_FOR.PER_ITEM : (item?.weightType || DETAILS_APPLY_FOR.TOTAL)
			const tiltableFlag = item?.tiltable ?? item?.turnable ?? (item?.nonTiltable !== undefined ? !item.nonTiltable : undefined)
			const stackableFlag = item?.stackable ?? (item?.nonStackable !== undefined ? !item.nonStackable : undefined)
			const craneLiftableFlag = item?.craneLiftable ?? (item?.nonCraneLiftable !== undefined ? !item.nonCraneLiftable : undefined)
			const forkLiftableFlag = item?.forkLiftable ?? (item?.nonForkLiftable !== undefined ? !item.nonForkLiftable : undefined)
			const fragileFlag = item?.fragile

			const parsedStackable = toBooleanFromFlag(stackableFlag)
			const parsedTiltable = toBooleanFromFlag(tiltableFlag)
			const parsedCraneLiftable = toBooleanFromFlag(craneLiftableFlag)
			const parsedForkLiftable = toBooleanFromFlag(forkLiftableFlag)
			const parsedFragile = toBooleanFromFlag(fragileFlag)
			const perPieceWeight =
				derivedWeightType === DETAILS_APPLY_FOR.PER_ITEM
					? totalWeight
					: pieces > 0
						? totalWeight / pieces
						: totalWeight

			return createEmptyRow({
				pieces: String(item?.numberOfPieces || item?.pieces || ''),
				length: String(
					item?.length ?? item?.lenth ?? item?.lengthPerPiece ?? ''
				),
				width: String(item?.width ?? item?.widthPerPiece ?? ''),
				height: String(
					item?.height ?? item?.heightPerpiece ?? item?.heightPerPiece ?? ''
				),
				dimensionUnit: item?.dimensionUnit || 'C',
				weight: Number.isFinite(perPieceWeight)
					? String(Number(perPieceWeight.toFixed(2)))
					: '',
				weightUnit: item?.weightUnit || item?.weightPerPieceUnit || defaultWeightUnit || 'K',
				weightType: DETAILS_APPLY_FOR.PER_ITEM,
				stackable: parsedStackable === null ? true : parsedStackable,
				turnable: parsedTiltable === null ? false : parsedTiltable,
				craneLiftable: parsedCraneLiftable,
				forkLiftable: parsedForkLiftable,
				fragile: parsedFragile === null ? false : parsedFragile,
				packingType:
					item?.packingType && item?.packingType !== ''
						? item.packingType
						: 'Select',
			})
		})
	}

	const mapTransitToRouteRows = (transitAirports = []) => {
		if (!Array.isArray(transitAirports) || transitAirports.length === 0)
			return []

		return transitAirports.map((item, index) => ({
			id: Date.now() + index,
			airport: item?.airportCode
				? { code: item.airportCode, name: item.airportCode }
				: null,
			carrier: item?.carrierCode || '',
		}))
	}

	useEffect(() => {
		const userDetails = getCurrentUser()
		setUserDetails(userDetails)
		const defaultCustomerCode = location.state?.customerCode || userDetails?.customerCode || userDetails?.agentCode;
		setAgentCode(defaultCustomerCode);
		if (defaultCustomerCode && !isEditMode) {
			validateAgentCode(defaultCustomerCode, userDetails)
		}
	}, [])

	useEffect(() => {
		window.scrollTo(0, 0)
			; (async () => {
				try {
					let items = await getShippingItems()
					if (items) {
						items = items
							.map((item) => ({
								...item,
								description:
									item.description
										?.toLowerCase()
										?.split(' ')
										?.map((w) => w?.charAt(0)?.toUpperCase() + w.slice(1))
										?.join(' ') || item.description,
							}))
							.sort((a, b) =>
								a.description < b.description
									? -1
									: a.description > b.description
										? 1
										: 0
							)
					}
					setShippingItems(items || [])
				} catch (err) {
					console.error('Failed to fetch shipping items', err)
				}
			})()
	}, [])

	// Fetch SLR details from API on mount if in edit mode and Redux is empty
	useEffect(() => {
		if (!isEditMode || !editSlrNumber) {
			return
		}
		if (slrBookingData && Object.keys(slrBookingData).length > 0) {
			return
		}

		const fetchSlrDetails = async () => {
			try {
				let customerCode = location.state?.customerCode || slrBookingData?.agentCode || userDetails?.customerCode || userDetails?.agentCode;
				const response = await getSlrBookingDetailsResponse(editSlrNumber, customerCode)

				if (response?.status === 200 && response?.data) {
					dispatch(slrBookingDetailsSuccess(response.data))
				} else {
					console.error('[SlrBookingInputFields] Invalid SLR response structure')
					dispatch(slrBookingDetailsError('Failed to fetch SLR details'))
				}
			} catch (error) {
				console.error('[SlrBookingInputFields] Failed to load SLR booking details:', error)
				dispatch(slrBookingDetailsError(error.message))
			}
		}

		fetchSlrDetails()
	}, [])

	useEffect(() => {
		if (!isEditMode || editPrefillAppliedRef.current) {
			return
		}

		const prefill = slrBookingData

		if (!prefill || Object.keys(prefill).length === 0) {
			return
		}

		editPrefillAppliedRef.current = true
		initialAgentDetailsRef.current = {
			agentCode: String(prefill?.agentCode || '').trim(),
			submittedBy: prefill?.submittedBy
				? { ...prefill.submittedBy }
				: { name: null, email: null, phoneNumber: null },
		}

		const originCode = prefill?.origin
		const destinationCode = prefill?.destination
		if (originCode) {
			setSelectedOrigin({ code: originCode, name: originCode })
			getAirportsByCode(originCode)
				.then((originAirport) => {
					if (originAirport) {
						setSelectedOrigin(originAirport)
					}
				})
				.catch(() => { })
		}
		if (destinationCode) {
			setSelectedDestination({ code: destinationCode, name: destinationCode })
			getAirportsByCode(destinationCode)
				.then((destinationAirport) => {
					if (destinationAirport) {
						setSelectedDestination(destinationAirport)
					}
				})
				.catch(() => { })
		}

		const parsedShippingDate = parsePrefillShippingDate(prefill?.shippingDate)
		if (parsedShippingDate) {
			setShippingDate(parsedShippingDate)
		}

		// Set shipping description from cargoDetails
		if (prefill?.cargoDetails?.shipmentDescription) {
			setShippingDescription(prefill.cargoDetails.shipmentDescription)
		}

		// Set airway bill number from cargoDetails or with fallback to prefixes
		const awbNumber = prefill?.cargoDetails?.awbNumber || prefill?.awbNumber || ''
		const awbPrefix = prefill?.awbPrefix || ''
		if (awbPrefix || awbNumber) {
			setAirwayBillNumber(`${awbPrefix}${awbNumber}`)
		}

		const existingAttachments = Array.isArray(prefill?.attachments)
			? prefill.attachments.map((attachment) => ({
				...attachment,
				name: attachment.fileName || attachment.name,
			}))
			: []
		setAttachments(existingAttachments)
		setHasAttachments(existingAttachments.length > 0)

		if (prefill?.weightUnit) {
			setWeightUnit(prefill.weightUnit)
		}

		if (prefill?.cargoDetails?.grossWeight != null) {
			setTotalWeight(String(prefill.cargoDetails.grossWeight))
		}

		if (prefill?.cargoDetails?.volume != null) {
			// Volume is available if needed
		}

		if (prefill?.agentCode) {
			setAgentCode(prefill.agentCode)
			lastValidatedAgentCodeRef.current = String(prefill.agentCode).trim()
		}
		setValidatedAgentDetails({
			companyName: prefill?.submittedBy?.name || '',
			userName: prefill?.submittedBy?.name || '',
			email: prefill?.submittedBy?.email || '',
			contactNo: prefill?.submittedBy?.phoneNumber || '',
		})
		setShowSubmittedBy(true)

		const prefillSccCodes = prefill?.cargoDetails?.sccCodes || []
		prefillSccCodesRef.current = prefillSccCodes

		const mappedRows = mapLooseDetailsToRows(
			prefill?.dimensionDetails || [],
			prefill?.weightUnit || 'K'
		)
		setPackagingRows(mappedRows)
		initialCargoValuesRef.current = {
			totalWeight: prefill?.cargoDetails?.grossWeight,
			weightUnit: prefill?.weightUnit,
			rows: mappedRows,
		}

		const allStackable = mappedRows.every((row) => row.stackable === true)
		const allTiltable = mappedRows.every((row) => row.turnable === true)
		const allCraneLiftable = mappedRows.every(
			(row) => row.craneLiftable === true
		)
		const allForkLiftable = mappedRows.every((row) => row.forkLiftable === true)
		const allFragile = mappedRows.every((row) => row.fragile === true)
		setIsStackable(allStackable)
		setIsTiltable(allTiltable)
		setIsCraneLiftable(allCraneLiftable)
		setIsForkLiftable(allForkLiftable)
		setIsFragile(allFragile)
		applyPrefilledSccState(null)

		// Populate transit airports with full details (city name + code)
		const transitAirports = prefill?.transitAirports || []
		const initialRouteRows = mapTransitToRouteRows(transitAirports)
		setCargoRouteRows(initialRouteRows)
		if (Array.isArray(transitAirports) && transitAirports.length > 0) {
			Promise.all(
				transitAirports.map(transit =>
					getAirportsByCode(transit.airportCode)
						.catch(error => {
							console.error(`[SlrBookingInputFields] Failed to fetch airport ${transit.airportCode}:`, error)
							// Return fallback with code only
							return { code: transit.airportCode, name: transit.airportCode }
						})
				)
			)
				.then(resolvedAirports => {
					setCargoRouteRows((prev) => prev.map((row) => {
						const index = initialRouteRows.findIndex((initialRow) => initialRow.id === row.id)
						if (index < 0 || !resolvedAirports[index] || row.airport !== initialRouteRows[index].airport) {
							return row
						}
						return { ...row, airport: resolvedAirports[index] }
					}))
				})
				.catch(error => {
					console.error('[SlrBookingInputFields] Failed to populate transit routes:', error)
				})
		}
	}, [isEditMode, slrBookingData])

	useEffect(() => {
		if (
			!isEditMode ||
			shippingItemPrefillAppliedRef.current ||
			!slrBookingData ||
			Object.keys(slrBookingData).length === 0 ||
			!shippingItems.length ||
			shippingItem
		) {
			return
		}

		const matchedShippingItem = resolveShippingItemFromPrefill(
			shippingItems,
			slrBookingData,
			slrBookingData
		)

		if (matchedShippingItem) {
			shippingItemPrefillAppliedRef.current = true
			skipShippingItemResetRef.current = true
			setShippingItem(matchedShippingItem)
			applyPrefilledSccState(matchedShippingItem)
		}
	}, [
		isEditMode,
		slrBookingData,
		shippingItems,
		shippingItem,
	])

	useEffect(() => {
		const getDefaultOriginValue = async () => {
			try {
				let defaultAirport = null
				const fallbackStation = getSlrFallbackStation()

				// If GSA User Agent, prioritize agentDefaultAirportCode
				if (fallbackStation) {
					defaultAirport = fallbackStation
				} else if (agentDetails?.agentDefaultAirportCode) {
					defaultAirport = agentDetails.agentDefaultAirportCode
				} else if (agentDetails?.agentStationCode) {
					defaultAirport = agentDetails.agentStationCode
				} else if (userDetails?.defaultAirportCode) {
					defaultAirport = userDetails.defaultAirportCode
				} else if (userDetails?.stationCode) {
					defaultAirport = userDetails.stationCode
				}

				if (defaultAirport) {
					const origin = await getAirportsByCode(defaultAirport)
					if (origin) {
						setDefaultOrigin(origin)
						// For edit flow, selected origin comes from the booking being modified.
						if (
							!isEditMode &&
							(fallbackStation || (!getFromSessionStorage('searchObject') &&
							!getFromSessionStorage('flightEnquiryObj')))
						) {
							setSelectedOrigin(origin)
						}
					}
				}
			} catch (error) {
				console.log('Error fetching default origin:', error)
			}
		}

		if (userDetails || agentDetails || getSlrFallbackStation()) {
			getDefaultOriginValue()
		}
	}, [userDetails, agentDetails, isEditMode])

	// Keep SCC state in sync with selected shipping item.
	useEffect(() => {
		if (skipShippingItemResetRef.current) {
			skipShippingItemResetRef.current = false
			return
		}

		setAdditionalSccs([])
		setAppliedSccs([])
		setAppliedServices([])
		setSccWarning(null)
		setSccCompatibilityError(null)
		setSccCombinationError(null)

		if (shippingItem?.code === 'APHARMA') {
			setAdditionalInfoData((prev) => ({
				...prev,
				type: 'pharma',
				pharma: {
					...prev.pharma,
					shippingPharmaType: PHARMA_SHIPPING_TYPE.ACTIVE,
				},
			}))
			return
		}

		setAdditionalInfoData({
			type: null,
			pharma: getInitPharmaObject(),
		})
	}, [shippingItem])

	const handleAddRow = () => {
		setHasEditedPackaging(true)
		setPackagingRows((prev) => [
			...prev,
			createEmptyRow({
				dimensionUnit: prev[0]?.dimensionUnit || 'C',
				weightUnit: prev[0]?.weightUnit || 'K',
				weightType: DETAILS_APPLY_FOR.PER_ITEM,
				stackable: isStackable === true,
				turnable: isTiltable === true,
				craneLiftable: isCraneLiftable,
				forkLiftable: isForkLiftable,
				fragile: isFragile === true,
			}),
		])
	}

	const handleRemoveRow = (index) => {
		setHasEditedPackaging(true)
		setPackagingRows((prev) => prev.filter((_, i) => i !== index))
		setPackagingRowsErrors((prev) => prev.filter((_, i) => i !== index))
	}

	const handleRowChange = (index, field, value) => {
		setHasEditedPackaging(true)
		setPackagingRows((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], [field]: value }

			// If first row's dimensionUnit changes, sync to all other rows
			if (index === 0 && field === 'dimensionUnit') {
				updated.forEach((row, i) => {
					if (i !== 0) {
						row.dimensionUnit = value
					}
				})
			}

			// Sync row-level flags back to the top-level radios
			if (field === 'stackable') {
				setIsStackable(
					updated.every((r) => r.stackable === true) ? true : false
				)
			}
			if (field === 'turnable') {
				setIsTiltable(updated.every((r) => r.turnable === true) ? true : false)
			}
			if (field === 'craneLiftable') {
				setIsCraneLiftable(
					updated.every((r) => r.craneLiftable === true) ? true : false
				)
			}
			if (field === 'forkLiftable') {
				setIsForkLiftable(
					updated.every((r) => r.forkLiftable === true) ? true : false
				)
			}
			if (field === 'fragile') {
				setIsFragile(updated.every((r) => r.fragile === true) ? true : false)
			}
			return updated
		})

		// Clear per-row field error
		if (packagingRowsErrors[index]) {
			setPackagingRowsErrors((prev) => {
				const updated = [...prev]
				if (updated[index]) updated[index] = { ...updated[index], [field]: '' }
				return updated
			})
		}
	}

	const onDrop = async (acceptedFiles, fileRejections = []) => {
		if (attachmentUploadRef.current || isSubmitting || (acceptedFiles.length === 0 && fileRejections.length === 0)) return
		attachmentUploadRef.current = true
		setIsUploadingAttachments(true)
		setAttachmentError('')
		const uploadedFiles = []
		const failedFiles = fileRejections.map(() => t('COMMON.FILE_SIZE_LIMIT', { maxSize: 2 }))
		try {
			for (const file of acceptedFiles) {
				if (!/\.(pdf|doc|docx|xls|xlsx|xlx|jpeg|jpg|png|txt)$/i.test(file.name)) {
					failedFiles.push(t('COMMON.FILE_INVALID_FORMAT', { fileName: file.name }))
					continue
				}
				if ([...attachments, ...uploadedFiles].some(attachment =>
					(attachment.fileName || attachment.name) === file.name)) {
					failedFiles.push(t('COMMON.FILE_ALREADY_EXISTS', { fileName: file.name }))
					continue
				}
				try {
					const uploaded = await saveSlrAttachment(file, uuidv4())
					uploadedFiles.push({ ...uploaded, name: uploaded.fileName || file.name })
				} catch (error) {
					failedFiles.push(t('COMMON.FILE_UPLOAD_FAILED', { fileName: file.name }))
				}
			}
			if (uploadedFiles.length > 0) {
				setAttachments(prev => [...prev, ...uploadedFiles])
			}
			if (failedFiles.length > 0) {
				setNotification({ severity: 'error', summary: t('COMMON.ERROR'), detail: [...new Set(failedFiles)].join('; ') })
			} else if (uploadedFiles.length > 0) {
				setNotification({ severity: 'success', summary: t('COMMON.SUCCESS'), detail: t('COMMON.FILE_UPLOAD_SUCCESS') })
			}
		} finally {
			attachmentUploadRef.current = false
			setIsUploadingAttachments(false)
		}
	}

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		multiple: true,
		maxSize: 2 * 1024 * 1024,
		disabled: isUploadingAttachments || isSubmitting,
	})

	const handleRemoveAttachment = (index) => {
		setAttachments((prev) => prev.filter((_, i) => i !== index))
	}

	const validateForm = () => {
		let isValid = true
		setAdditionalPropsError('')
		setPackagingRowsErrors([])
		setAttachmentError('')

		// Validate shipping details
		const newShippingErrors = {}
		if (!selectedOrigin) {
			newShippingErrors.origin = 'ER001'
			isValid = false
		}
		if (!selectedDestination) {
			newShippingErrors.destination = 'ER002'
			isValid = false
		}
		if (!shippingDate) {
			newShippingErrors.shippingDate = 'ER003'
			isValid = false
		}
		setShippingErrors(newShippingErrors)

		// Validate cargo details
		const newCargoErrors = {}
		if (!shippingItem) {
			newCargoErrors.shippingItem = MESSAGES.INVALID_SHIPPING_ITEM
			isValid = false
		}
		if (!totalWeight || Number(totalWeight) === 0) {
			newCargoErrors.totalWeight = 'ER009'
			isValid = false
		}
		if (cargoErrors.agentCode) {
			newCargoErrors.agentCode = cargoErrors.agentCode
			isValid = false
		} else if (!agentCode || !agentCode.trim()) {
			newCargoErrors.agentCode = 'ER159'
			isValid = false
		} else if (!validatAgentCodePattern(agentCode)) {
			newCargoErrors.agentCode = t(MESSAGES.VALID_AGENT_CODE, { ns: 'errors' })
			isValid = false
		}
		setCargoErrors(newCargoErrors)

		const hasSubmittedByErrors = !!(
			!newCargoErrors.agentCode &&
			showSubmittedBy &&
			validatedAgentDetails &&
			!validateSubmittedBy()
		)

		if (
			isCraneLiftable === null ||
			isForkLiftable === null ||
			isFragile === null
		) {
			setAdditionalPropsError('ER298')
			isValid = false
		}

		const rowErrors = packagingRows.map((row) => {
			const errors = {}
			if (!row.pieces || Number(row.pieces) === 0) {
				errors.pieces = 'ER005'
				isValid = false
			}
			if (!row.length || Number(row.length) === 0) {
				errors.length = 'ER006'
				isValid = false
			}
			if (!row.width || Number(row.width) === 0) {
				errors.width = 'ER007'
				isValid = false
			}
			if (!row.height || Number(row.height) === 0) {
				errors.height = 'ER008'
				isValid = false
			}
			if (!row.weight || Number(row.weight) === 0) {
				errors.weight = 'ER009'
				isValid = false
			}
			if (!row.packingType || row.packingType === 'Select') {
				errors.packingType = 'ER299'
				isValid = false
			}
			return errors
		})
		setPackagingRowsErrors(rowErrors)

		if (hasAttachments && attachments.length === 0) {
			setAttachmentError('ER236')
			isValid = false
		}

		if (cargoRouteRows.length > 0) {
			const errors = {}
			cargoRouteRows.forEach((row) => {
				const rowError = {}
				if (!row.airport?.code) rowError.airport = true
				if (!row.carrier?.trim()) rowError.carrier = true
				if (Object.keys(rowError).length > 0) {
					errors[row.id] = rowError
					isValid = false
				}
			})
			setRouteRowErrors(errors)
		} else {
			setRouteRowErrors({})
		}

		if (!isValid) {
			window.scrollTo(0, 0)
		} else if (hasSubmittedByErrors) {
			document
				.getElementById('slr-submitted-by-section')
				?.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}

		return isValid && !hasSubmittedByErrors
	}


	/**
	 * Calls the classification API and returns the response
	 * Shows appropriate popup based on classification type
	 * SLR: Skips popup and continues with booking
	 * ODF/ODP/NORMAL: Shows alert that info doesn't fall under SLR
	 * @returns {Promise<Object>} Classification response with type and chargeableWeight
	 */
	const findClassification = async (agentCode) => {
		setIsSubmitting(true)
		try {
			const classificationResult = await classifyShipment(packagingRows, agentCode, {
				operationFlag: isEditMode ? 'U' : 'I',
				totalWeight,
				weightUnit,
			})
			if (classificationResult) {
				setClassificationData(classificationResult)
				const classificationType = classificationResult?.type
				if (classificationType === 'SLR') {
					// For SLR: Skip popup and directly proceed with booking
					console.log('SLR Classification - Proceeding with booking')
					return classificationResult
				} else {
					// For ODF/ODP/NORMAL: Show alert that doesn't fall under SLR
					setClassificationAlertData(classificationResult)
					setShowNonSlrAlert(true)
					return null // Stop booking flow - wait for user to update
				}
			} else {
				setProcessErrorMessage(t(MESSAGES.PROCESS_REQUEST_FAILED, { ns: "errors" }))
				return null
			}
		} catch (classificationError) {
			setProcessErrorMessage(t(MESSAGES.PROCESS_REQUEST_FAILED, { ns: "errors" }))
			return null
		}
	}

	const handleNonSlrAlertOk = () => {
		setShowNonSlrAlert(false)
		setClassificationAlertData(null)
		setIsSubmitting(false)
	}

	const getAllowedStationCodes = (currentUserDetails) => {
		const fallbackStation = getSlrFallbackStation()
		if (fallbackStation) return [fallbackStation]
		const stationCodes = []
		if (currentUserDetails) {
			if (currentUserDetails?.stationCode) {
				stationCodes.push(currentUserDetails.stationCode)
			}
			if (Array.isArray(currentUserDetails?.userAllowedStations)) {
				currentUserDetails.userAllowedStations.forEach((allowedStation) => {
					if (
						(allowedStation?.additionalRoleGroup?.includes('GSA') ||
							allowedStation?.stationDefaultRoleGroup === 'GSA') &&
						allowedStation?.stationCode
					) {
						stationCodes.push(allowedStation.stationCode)
					}
				})
			}
		}
		return stationCodes
	}

	const handleAgentCodeFocus = () => {
		agentValidationRequestRef.current++
		setIsValidatingAgent(false)
		clearCargoError('agentCode')
	}

	const validateAgentCode = async (agentCodeToValidate, currentUserDetails) => {
		const code = String(agentCodeToValidate || '').trim()

		if (!code) {
			setValidatedAgentDetails(null)
			setCargoErrors((prev) => ({ ...prev, agentCode: 'ER159' }))
			return
		}

		if (!validatAgentCodePattern(code)) {
			setValidatedAgentDetails(null)
			setCargoErrors((prev) => ({
				...prev,
				agentCode: t(MESSAGES.VALID_AGENT_CODE, { ns: 'errors' }),
			}))
			return
		}

		// Same code as the last successful validation: keep the existing details
		if (validatedAgentDetails && lastValidatedAgentCodeRef.current === code) {
			setShowSubmittedBy(true)
			return
		}

		const requestId = ++agentValidationRequestRef.current
		const showAccessError = () => {
			setValidatedAgentDetails(null)
			setCargoErrors((prev) => ({
				...prev,
				agentCode: t(MESSAGES.AGENT_CODE_ACCESS_ERROR, { ns: 'errors' }),
			}))
		}

		setIsValidatingAgent(true)
		try {
			const agentListResponse = await getStationByAgentCode({
				customer: code,
				companyCode: 'EY',
			})
			if (requestId !== agentValidationRequestRef.current) {
				return
			}

			if (
				!agentListResponse ||
				agentListResponse?.errorDetailApiDto ||
				(getPrincipal()?.type !== 'enterprise' &&
					!getAllowedStationCodes(currentUserDetails).includes(agentListResponse?.stationCode))
			) {
				showAccessError()
				return
			}

			lastValidatedAgentCodeRef.current = code
			clearCargoError('agentCode')
			setSubmittedByErrors({})
			setValidatedAgentDetails({
				...agentListResponse,
				companyName: agentListResponse?.companyName || agentListResponse?.userName || '',
				userName: agentListResponse?.companyName || agentListResponse?.userName || '',
			})
			setShowSubmittedBy(true)
		} catch (error) {
			if (requestId === agentValidationRequestRef.current) {
				showAccessError()
			}
		} finally {
			if (requestId === agentValidationRequestRef.current) {
				setIsValidatingAgent(false)
			}
		}
	}

	const handleAgentCodeBlur = () => validateAgentCode(agentCode, userDetails)

	const handleSubmittedByChange = (field, value) => {
		setValidatedAgentDetails((prev) => {
			const next = { ...(prev || {}) }
			if (field === 'name') {
				next.companyName = value
				next.userName = value
			} else if (field === 'email') {
				next.email = value
			} else if (field === 'phone') {
				next.contactNo = value.replace(/\D/g, '')
			}
			return next
		})
		if (field !== 'phone') {
			setSubmittedByErrors((prev) => ({ ...prev, [field]: '' }))
		}
	}

	const validateSubmittedBy = () => {
		const errors = {}
		if (!String(validatedAgentDetails?.companyName || '').trim()) {
			errors.name = MESSAGES.FIELD_REQUIRED
		}
		const email = String(validatedAgentDetails?.email || '').trim()
		if (!email) {
			errors.email = MESSAGES.FIELD_REQUIRED
		} else if (!emailPattern.test(email)) {
			errors.email = MESSAGES.ENTER_VALID_MAIL
		}
		setSubmittedByErrors(errors)
		return Object.keys(errors).length === 0
	}

	/**
	 * Performs the actual SLR booking submission
	 * Called after classification alerts are handled
	 * Handles both create and update (edit mode) scenarios
	 */
	const performSlrBookingSubmission = async (agentDetails) => {
		try {
			const sccCodesArray = [...new Set([shippingItem?.scc, ...[...appliedSccs, ...additionalSccs, ...appliedServices].map(item => item.scc)].filter(Boolean))]
			const transitAirports = cargoRouteRows
				.filter((row) => row?.airport?.code || row?.carrier)
				.map((row) => ({
					airportCode: row?.airport?.code || null,
					carrierCode: row?.carrier?.trim().toUpperCase() || null,
				}))

			const shipment = {
				origin: selectedOrigin,
				destination: selectedDestination,
				shippingDate: shippingDate
					? moment(shippingDate).format('DD-MMM-YYYY')
					: null,
				shippingItem,
				totalWeight,
				weightUnit,
				packagingRows,
				isStackable,
				isTiltable,
				shippingDescription,
				airwayBillNumber,
				agentCode,
				isCraneLiftable,
				isForkLiftable,
				isFragile,
				attachments,
				customerCode: agentCode,
				transitAirports, // Include routing details for both create and update
				slrReferenceNumber: isEditMode ? slrBookingData?.slrReferenceNumber : undefined,
				slrId: isEditMode ? slrBookingData?.slrId : undefined,
				sccCodes: [...appliedSccs, ...additionalSccs, ...appliedServices].filter(
					(item, index, items) => items.findIndex(existing => existing.scc === item.scc) === index
				),
			}
			const response = await submitSlrBooking(shipment, true, isEditMode ? slrBookingData?.slrId : null, agentDetails)
			if (response?.status === 200) {
				setResultData({
					success: true,
					slrNumber: response.data?.slrReferenceNumber || editSlrNumber,
				})
				if (!isEditMode) { setShowResultPopup(true) }
				const slrReferenceNumber = response.data?.slrReferenceNumber || editSlrNumber

				const shouldCallCargoStack = !isEditMode || hasCargoValuesChanged(
					initialCargoValuesRef.current,
					{ totalWeight, weightUnit, rows: packagingRows }
				)
				if (shouldCallCargoStack) {
					const cargoStackRequest = submitSlrCargoStack(response.data, {
						...shipment,
						slrReferenceNumber,
					}).catch(error => {
						console.error('[SlrBookingInputFields] Background CargoStack request failed:', error)
					})
					if (isEditMode) {
						await cargoStackRequest
					}
				}

				// === TRIGGER EMAIL NOTIFICATIONS (Only for new SLR submissions, not edits) ===
				if (!isEditMode) {
					const emailConfig = {
						agentName: agentDetails?.userName,
						agentPhone: agentDetails?.contactNo || '',
						agentEmail: agentDetails?.email,
						customerEmail: agentDetails?.email,
						customerDetails: `${agentDetails?.userName ? `${agentDetails?.userName} / ` : ''}${agentCode}`,
						agentCode,
						shippingDescription: shippingDescription,
					}
					sendSlrNotificationEmails(response, packagingRows, shippingDate, sccCodesArray, userDetails, emailConfig)
						.catch(error => {
							console.error('[SlrBookingInputFields] Background email notification failed:', error)
						})
				}
				if (isEditMode) {
					// Store the result in redux so the view screen can render the success alert after redirect
					dispatch(setSlrBookingResult({ success: true, slrNumber: slrReferenceNumber }))
					history.push(
						`/slr-bookings/view/${encodeURIComponent(slrReferenceNumber)}`,
						{ customerCode: agentCode }
					)
				}
			} else {
				setResultData({ success: false })
				if (!isEditMode) { setShowResultPopup(true) }
				if (isEditMode) {
					dispatch(setSlrBookingResult({ success: false }))
					history.push(
						`/slr-bookings/view/${encodeURIComponent(editSlrNumber)}`,
						{ customerCode: initialAgentDetailsRef.current?.agentCode }
					)
				}
			}
		} catch (error) {
			setResultData({ success: false, errorMessage: error?.response?.data?.errorDescription })
			if (!isEditMode) { setShowResultPopup(true) }
			if (isEditMode) {
				dispatch(setSlrBookingResult({ success: false }))
				history.push(
					`/slr-bookings/view/${encodeURIComponent(editSlrNumber)}`,
					{ customerCode: initialAgentDetailsRef.current?.agentCode }
				)
			}
		} finally {
			setIsSubmitting(false)

		}
	}

	const handleConfirm = async () => {
		if (attachmentUploadRef.current) return
		if (!validateForm()) {
			return
		}

		if (isValidatingAgent) {
			return
		}

		const trimmedAgentCode = String(agentCode || '').trim()
		if (
			!validatedAgentDetails ||
			lastValidatedAgentCodeRef.current !== trimmedAgentCode
		) {
			setShowSubmittedBy(false)
			setCargoErrors((prev) => ({
				...prev,
				agentCode: t(MESSAGES.AGENT_CODE_ACCESS_ERROR, { ns: 'errors' }),
			}))
			window.scrollTo(0, 0)
			return
		}

		setIsSubmitting(true)
		const classificationResult = await findClassification(agentCode)

		if (classificationResult === null) {
			setIsSubmitting(false)
			return
		}

		// If we reach here, classification is NORMAL - proceed with booking
		await performSlrBookingSubmission({
			...validatedAgentDetails,
			companyName: String(validatedAgentDetails.companyName).trim(),
			userName: String(validatedAgentDetails.companyName).trim(),
			email: String(validatedAgentDetails.email).trim(),
			contactNo: String(validatedAgentDetails.contactNo || '').trim(),
		})
	}

	const handleResultOk = () => {
		setShowResultPopup(false)
		if (resultData?.success) {
			history.push('/slr-bookings')
		}
	}

	const handleBack = () => {
		if (isEditMode) {
			history.push(
				`/slr-bookings/view/${editSlrNumber}`,
				{ customerCode: slrBookingData?.agentCode || agentCode }
			)
			return
		}
		history.push('/slr-bookings')
	}

	const weightSummary = useMemo(() => {
		const hasAnyData = packagingRows.some(
			(r) => r.pieces && r.weight && r.length && r.width && r.height
		)
		if (isEditMode && !hasAnyData) return null

		const weightUnit = packagingRows[0]?.weightUnit || 'K'
		const dimensionUnit = packagingRows[0]?.dimensionUnit || 'C'

		const looseDetails = packagingRows.map((r) => ({
			pieces: r.pieces,
			length: r.length,
			width: r.width,
			height: r.height,
			dimensionUnit: r.dimensionUnit || 'C',
			weight: r.weight,
			weightUnit: r.weightUnit || 'K',
			weightType: r.weightType || DETAILS_APPLY_FOR.PER_ITEM,
			stackable: r.stackable !== false,
			turnable: r.turnable === true,
			craneLiftable: r.craneLiftable === true,
			forkLiftable: r.forkLiftable === true,
			fragile: r.fragile === true,
		}))

		const totalPieces = packagingRows.reduce(
			(totalCount, row) => totalCount + (Number(row.pieces) || 0),
			0
		)

		const totalWeightInKg = packagingRows.reduce((totalWeight, row) => {
			const rowWeight = parseFloat(row.weight) || 0
			const pieceCount = Number(row.pieces) || 0
			const rowWeightInKg =
				parseFloat(calculateWeightToKg(rowWeight, row.weightUnit || 'K')) || 0
			return (
				totalWeight +
				(row.weightType === DETAILS_APPLY_FOR.PER_ITEM
					? rowWeightInKg * pieceCount
					: rowWeightInKg)
			)
		}, 0)

		const computedTotalVolumeInMtrs = packagingRows.reduce(
			(totalVolume, row) => {
				const lengthInMeters =
					parseFloat(
						calculateDimensionToCm(
							parseFloat(row.length) || 0,
							row.dimensionUnit || 'C'
						)
					) / 100
				const widthInMeters =
					parseFloat(
						calculateDimensionToCm(
							parseFloat(row.width) || 0,
							row.dimensionUnit || 'C'
						)
					) / 100
				const heightInMeters =
					parseFloat(
						calculateDimensionToCm(
							parseFloat(row.height) || 0,
							row.dimensionUnit || 'C'
						)
					) / 100
				const pieceCount = Number(row.pieces) || 0
				return (
					totalVolume +
					lengthInMeters * widthInMeters * heightInMeters * pieceCount
				)
			},
			0
		)

		const prefilledVolume = parseFloat(
			slrBookingData?.cargoDetails?.volume
		)
		const totalVolumeInMtrs =
			isEditMode &&
				!hasEditedPackaging &&
				Number.isFinite(prefilledVolume) &&
				prefilledVolume > 0
				? prefilledVolume
				: computedTotalVolumeInMtrs

		const chargeableWeight = Math.max(totalWeightInKg, totalVolumeInMtrs * 167)

		return {
			shippingType: SHIPPING_TYPE.LOOSE,
			weightUnit,
			dimensionUnit,
			totalPieces,
			totalWeightInKg,
			totalVolumeInMtrs,
			chargeableWeight,
			totalWeight: totalWeightInKg,
			looseDetails,
		}
	}, [packagingRows, slrBookingData, hasEditedPackaging, isEditMode])

	return (
		<div className='search-panel slr-search-panel w-100'>
			{/* <h3 className="slr-main-title">{t("BOOKING.SLR_BOOKING_TITLE")}</h3> */}

			<div className='new-booking-container'>
				<div className='form-container-shippingdetails'>
					<h3>{t('BOOKING.SHIPPING_DETAILS')}</h3>
					<div className='form-container'>
						{/* Origin & Destination */}
						<div className='shipping-deparr-row'>
							<div
								className={`form-group common-autocomplete ${shippingErrors.origin ? 'red-border' : ''
									}`}
							>
								<div className='p-float-label input-wrapper'>
									<AirportAutocomplete
										inputId='slr-origin-airport'
										autoCompleteType={AUTOCOMPLETE_TYPE.ORIGIN}
										selectedOrigin={selectedOrigin}
										selectedDestination={selectedDestination}
										setSelectedOrigin={(val) => {
											setSelectedOrigin(val)
											setShippingErrors((prev) => ({ ...prev, origin: null }))
										}}
										setSelectedDestination={setSelectedDestination}
										handleSetError={handleSetError}
										clearError={clearError}
										draftCallback={() => { }}
										setNotificationMessage={setNotificationMessage}
										setNotificationType={setNotificationType}
									/>
									<label htmlFor='slr-origin-airport'>
										{t('COMMON.SHP_FROM')}
									</label>
								</div>
								{shippingErrors.origin && (
									<span className='search-panel__errormsg'>
										{t(shippingErrors.origin, { ns: 'errors' })}
									</span>
								)}
							</div>

							<div
								className={`form-group common-autocomplete ${shippingErrors.destination ? 'red-border' : ''
									}`}
							>
								<div className='p-float-label input-wrapper'>
									<AirportAutocomplete
										inputId='slr-destination-airport'
										autoCompleteType={AUTOCOMPLETE_TYPE.DESTINATION}
										selectedOrigin={selectedOrigin}
										selectedDestination={selectedDestination}
										setSelectedDestination={(val) => {
											setSelectedDestination(val)
											setShippingErrors((prev) => ({
												...prev,
												destination: null,
											}))
										}}
										handleSetError={handleSetError}
										clearError={clearError}
										draftCallback={() => { }}
										setNotificationMessage={setNotificationMessage}
										setNotificationType={setNotificationType}
									/>
									<label htmlFor='slr-destination-airport'>
										{t('COMMON.SHP_TO')}
									</label>
								</div>
								{shippingErrors.destination && (
									<span className='search-panel__errormsg'>
										{t(shippingErrors.destination, { ns: 'errors' })}
									</span>
								)}
							</div>
						</div>

						{/* Shipping Date */}
						<div className='shipping-shpdate-row mb-2'>
							<div className='form-group calendar-group'>
								<AppCalendar
									id='slr-shipping-date'
									value={shippingDate}
									onChangeHandler={(date) => {
										setShippingDate(date)
										setShippingErrors((prev) => ({
											...prev,
											shippingDate: null,
										}))
									}}
									minDate={moment().toDate()}
									maxDate={moment()
										.add(ALLOWABLE_BOOKING_DATE - 1, 'days')
										.toDate()}
									label={t('COMMON.SHP_DATE')}
									error={
										shippingErrors.shippingDate
											? t(shippingErrors.shippingDate, { ns: 'errors' })
											: ''
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='new-booking-container'>
				<div className='form-container-shippingdetails'>
					<h3>{t('BOOKING.CARGO_DETAILS')}</h3>

					<div className='form-container'>
						{/* What are you shipping? & Additional Special Handling Codes */}
						<div className='shipping-deparr-row'>
							<div
								className={`form-group position-relative common-autocomplete ${cargoErrors.shippingItem ? 'red-border' : ''
									}`}
							>
								<div
									className={`p-float-label input-wrapper ${shippingItem ? 'p-filled' : ''
										}`}
								>
									<ShippingItemAutocomplete
										inputId={FORM_FIELDS.SHIPPING_ITEM}
										fieldType='SHIPPING_ITEM'
										selectedShippingItem={shippingItem}
										setSelectedShippingItem={(item) => {
											setShippingItem(item)
											clearCargoError('shippingItem')
										}}
										shippingItems={shippingItems}
										error={cargoErrors}
										clearError={clearCargoError}
										setError={(errors) =>
											setCargoErrors((prev) => ({ ...prev, ...errors }))
										}
										draftCallback={() => { }}
										hoverColor='#f9f9f9'
									/>
									<label htmlFor={FORM_FIELDS.SHIPPING_ITEM}>
										{t('COMMON.SHP_ITEM')}
									</label>
								</div>
								<span className='search-panel__errormsg'>
									{cargoErrors.shippingItem
										? t(cargoErrors.shippingItem, { ns: 'errors' })
										: ''}
								</span>
								{(shippingItem?.scc ||
									appliedSccs.length > 0 ||
									additionalSccs.length > 0 ||
									appliedServices.length > 0) && (
										<div className='scc-row'>
											<SccListDisplay
												nonRemovableSccs={[
													...(shippingItem?.scc
														? [
															{
																scc: shippingItem.scc,
																sccDescription:
																	shippingItem.sccDescription ||
																	shippingItem.scc,
															},
														]
														: []),
													...appliedSccs,
												]}
												additionalSccs={additionalSccs}
												appliedServices={appliedServices}
												handleRemoveAdditionalScc={handleRemoveScc}
												draftCallback={() => { }}
												autoAdd={autoAdd}
											/>
										</div>
									)}
							</div>

							{/* Additional Special Handling Codes */}
							<div
								className={`form-group common-autocomplete ${sccWarning ? 'red-border' : ''
									}`}
							>
								<div className='p-float-label input-wrapper'>
									<SccAutoComplete
										inputId='slr-additional-scc'
										handleAddAdditinalScc={handleAddScc}
										clearError={clearCargoError}
										setSccWarning={setSccWarning}
										setNotificationMessage={setNotificationMessage}
										setNotificationType={setNotificationType}
										handleSccChange={handleSccChange}
										setAutoAdd={setAutoAdd}
									/>
									<label htmlFor='slr-additional-scc'>
										{t('COMMON.ADD_SHC_OPT')}
									</label>
								</div>
								<span className='search-panel__errormsg'>{sccWarning}</span>
							</div>
						</div>

						{/* Total Weight */}
						<div className='cargo-weight-row'>
							<div
								className={`form-group input-with-dropdown ${cargoErrors.totalWeight ? 'red-border' : ''
									}`}
							>
								<CommonInput
									type='text'
									label={t('COMMON.ENTER_TOTAL_WEIGHT')}
									value={totalWeight}
									onChange={(e) => handleTotalWeightChange(e.target.value)}
									error={
										cargoErrors.totalWeight
											? t(cargoErrors.totalWeight, { ns: 'errors' })
											: ''
									}
								/>
								<CommonDropDown
									value={weightUnit}
									options={WEIGHT_UNIT_SELECT_ITEMS}
									onChange={(e) => setWeightUnit(e.target.value)}
									isNoLabel
								/>
							</div>
						</div>

						<div className='cargo-pieceschkbox-row position-relative slr-specify-radio-section'>
							<label className='sub-head-label mb-0'>
								{t('BOOKING.SLR_SPECIFY_IF_APPLICABLE')}
							</label>
							<div className='slr-inline-radio-group'>
								{/* Stackable */}
								<div className='slr-inline-radio-item'>
									<label className='slr-radio-label'>
										{t('COMMON.STACKABLE')}
									</label>
									<RadioToggle
										checkedArray={[isStackable === true, isStackable === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className='slr-stackable-toggle'
										onChangeArray={[
											() => {
												setIsStackable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, stackable: true }))
												)
											},
											() => {
												setIsStackable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, stackable: false }))
												)
											},
										]}
									/>
								</div>

								{/* Tiltable */}
								<div className='slr-inline-radio-item'>
									<label className='slr-radio-label'>
										{t('COMMON.TILTABLE')}
									</label>
									<RadioToggle
										checkedArray={[isTiltable === true, isTiltable === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className='slr-tiltable-toggle'
										onChangeArray={[
											() => {
												setIsTiltable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, turnable: true }))
												)
											},
											() => {
												setIsTiltable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, turnable: false }))
												)
											},
										]}
									/>
								</div>

								{/* Crane Liftable */}
								<div className='slr-inline-radio-item'>
									<label className='slr-radio-label'>
										{t('BOOKING.SLR_CRANE_LIFTABLE')}
									</label>
									<RadioToggle
										checkedArray={[
											isCraneLiftable === true,
											isCraneLiftable === false,
										]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className='slr-crane-toggle'
										onChangeArray={[
											() => {
												setIsCraneLiftable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, craneLiftable: true }))
												)
											},
											() => {
												setIsCraneLiftable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, craneLiftable: false }))
												)
											},
										]}
									/>
								</div>

								{/* Fork Liftable */}
								<div className='slr-inline-radio-item'>
									<label className='slr-radio-label'>
										{t('BOOKING.SLR_FORK_LIFTABLE')}
									</label>
									<RadioToggle
										checkedArray={[
											isForkLiftable === true,
											isForkLiftable === false,
										]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className='slr-fork-toggle'
										onChangeArray={[
											() => {
												setIsForkLiftable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, forkLiftable: true }))
												)
											},
											() => {
												setIsForkLiftable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, forkLiftable: false }))
												)
											},
										]}
									/>
								</div>

								{/* Fragile */}
								<div className='slr-inline-radio-item'>
									<label className='slr-radio-label'>
										{t('BOOKING.SLR_FRAGILE')}
									</label>
									<RadioToggle
										checkedArray={[isFragile === true, isFragile === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className='slr-fragile-toggle'
										onChangeArray={[
											() => {
												setIsFragile(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, fragile: true }))
												)
											},
											() => {
												setIsFragile(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) =>
													prev.map((r) => ({ ...r, fragile: false }))
												)
											},
										]}
									/>
								</div>
							</div>

							{additionalPropsError && (
								<span className='common-input-error'>
									{t(additionalPropsError, { ns: 'errors' })}
								</span>
							)}
						</div>

						<div className='cargo-pieceschkbox-row slr-packaging-details-section'>
							{/* <label className="sub-head-label mb-0">
							{t("BOOKING.SLR_VERIFY_SHIPMENT_DETAILS")}
						</label> */}

							<div className='slr-packaging-rows'>
								{packagingRows.map((row, idx) => (
									<SlrPackagingRow
										key={idx}
										row={row}
										rowIndex={idx}
										canRemove={packagingRows.length > 1}
										isStackable={isStackable}
										isTiltable={isTiltable}
										isCraneLiftable={isCraneLiftable}
										isForkLiftable={isForkLiftable}
										isFragile={isFragile}
										onChange={(field, value) =>
											handleRowChange(idx, field, value)
										}
										onRemove={() => handleRemoveRow(idx)}
										disableDimensionUnit={idx !== 0}
										errors={packagingRowsErrors[idx] || {}}
									/>
								))}
							</div>

							{/* + Add another */}
							<div className='cargo-addupload-row mb-0'>
								<div
									className='add-more-btn'
									onClick={handleAddRow}
									style={{ cursor: 'pointer' }}
								>
									<img
										className='add-more-plus'
										src={plusIcon}
										alt='plus'
										title='add-more-plus'
									/>
									<span className='add-more-link'>
										<span>{t('COMMON.ADD_ANOTHER')}</span>
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='new-booking-container'>
				<div className='form-container-shippingdetails'>
					<h3 className='page-sub-head-view-booking'>
						{t('BOOKING.SLR_ROUTING_DETAILS')}
					</h3>
					<div className='cargo-details-card-custom w-100'>
						{cargoRouteRows.map((row) => (
							<div key={row.id} className='cargo-route-row'>
								<div
									className={`form-group common-autocomplete slr-autocomplete ${routeRowErrors[row.id]?.airport ? 'red-border' : ''
										}`}
								>
									<div className='p-float-label input-wrapper'>
										<AirportAutocomplete
											autoCompleteType={AUTOCOMPLETE_TYPE.ORIGIN}
											selectedOrigin={row.airport}
											setSelectedOrigin={(airport) =>
												updateRouteRow(row.id, 'airport', airport)
											}
											selectedDestination={null}
											setSelectedDestination={() => { }}
											clearError={() => { }}
											handleSetError={() => { }}
											screen='MANAGE_BOOKING'
											displaySimilarBooking={false}
											draftCallback={() => { }}
											setNotLeave={() => { }}
										/>
										<label>{t('COMMON.AIRPORT_CODE')}</label>
									</div>
									<span className='search-panel__errormsg'>
										{routeRowErrors[row.id]?.airport
											? t('ER291', { ns: 'errors' })
											: ''}
									</span>
								</div>
								<div className='form-group'>
									<CommonInput
										type='text'
										label={t('CAPTURE_AWB.CARRIER_CODE')}
										value={row.carrier}
										onChange={(e) =>
											updateRouteRow(
												row.id,
												'carrier',
												e.target.value.toUpperCase()
											)
										}
										error={
											routeRowErrors[row.id]?.carrier
												? t('ER148', { ns: 'errors' })
												: ''
										}
									/>
								</div>
								<div
									className='cargo-row-remove-btn'
									onClick={() => removeRouteRow(row.id)}
								>
									<img src={deleteIcon} alt='Trash' title='Trash' />
								</div>
							</div>
						))}
						<div className='add-more-btn mt-1' onClick={addRouteRow}>
							<img
								className='add-more-plus'
								src={plusIcon}
								alt='plus'
								title='add-more-plus'
							/>
							<span className='add-more-link'>
								{t('BOOKING.SLR_ADD_TRANSIT')}
							</span>
						</div>
					</div>
				</div>
			</div>

			<div className='new-booking-container'>
				<div className='form-container-shippingdetails'>
						<SlrAdditionalDetailsSection
							t={t}
							shippingDescription={shippingDescription}
							setShippingDescription={setShippingDescription}
							hasAttachments={hasAttachments}
							setHasAttachments={setHasAttachments}
							setAttachments={setAttachments}
							setAttachmentError={setAttachmentError}
							attachments={attachments}
							handleRemoveAttachment={handleRemoveAttachment}
							isUploadingAttachments={isUploadingAttachments || isSubmitting}
							getRootProps={getRootProps}
							getInputProps={getInputProps}
							attachmentError={attachmentError}
							airwayBillNumber={airwayBillNumber}
							setAirwayBillNumber={setAirwayBillNumber}
							isEditMode={isEditMode}
							renderEditDetails={renderEditDetails}
						/>
				</div>
			</div>

			<SlrSubmittedBySection
				t={t}
				details={validatedAgentDetails}
				errors={submittedByErrors}
				onChange={handleSubmittedByChange}
				showDetails={showSubmittedBy && !!validatedAgentDetails}
				agentCode={agentCode}
				setAgentCode={(value) => {
					setAgentCode(value)
					clearCargoError('agentCode')
				}}
				agentCodeError={cargoErrors.agentCode}
				onAgentCodeFocus={handleAgentCodeFocus}
				onAgentCodeBlur={handleAgentCodeBlur}
			/>

			<SlrSummarySection weightSummary={weightSummary} />



			<SlrInstructionsSection t={t} />

			{(isSubmitting || isUploadingAttachments || isValidatingAgent) && <LoadingIndicator />}

			<CustomToast
				displayData={notification}
				setDisplayData={setNotification}
			/>

			<AlertMessage
				message={warning && t(MESSAGES.ADD_SERVICE_INFO, { ns: 'errors' })}
				onClose={() => setWarning(false)}
				isPopup
			/>

			<AlertMessage
				type='error'
				message={sccErrorMessage}
				onClose={handleCloseSccError}
				isPopup
			/>

			<AlertMessage
				type='error'
				message={processErrorMessage}
				onClose={() => setProcessErrorMessage(null)}
				isPopup
			/>

			<div className='slr-footer-buttons'>
				<SecondaryButton label={t('COMMON.BACK_BUTTON')} onClick={handleBack} disabled={isSubmitting || isUploadingAttachments} />
				<PrimaryButton label={t('COMMON.CONFIRM')} onClick={handleConfirm} disabled={isSubmitting || isUploadingAttachments} />
			</div>

			{showNonSlrAlert && classificationAlertData && (
				<MiniPopup
					type='Warning'
					mainMsg={t('BOOKING.SLR_NON_SLR_MSG')}
					subMsg={t('BOOKING.SLR_NON_SLR_SUB_MSG')}
					onConfirm={handleNonSlrAlertOk}
					showCancelButton={false}
				/>
			)}

			<CustomPopup
				isVisible={showResultPopup}
				headerComponent={{
					mainHeader: resultData?.success
						? t('BOOKING.SLR_RESULT_SUCCESS_TITLE')
						: t('BOOKING.SLR_RESULT_ERROR_TITLE'),
				}}
				footerPriBtnLabel={t('COMMON.OK')}
				footerPriBtnClick={handleResultOk}
				onClose={() => setShowResultPopup(false)}
			>
				<div className='delete-confirm-msg-row'>
					{resultData?.success ? (
						resultData?.slrNumber && (
							<div className='delete-confirm-msg mb-2'>
								{t('BOOKING.SLR_RESULT_NUMBER')}:{' '}
								<span className='slr-bold'>{resultData.slrNumber}.</span>
							</div>
						)
					) : (
						<div className='delete-confirm-msg mb-2'>
							{resultData?.errorMessage || t('BOOKING.SLR_RESULT_ERROR_SUBTITLE')}
						</div>
					)}
				</div>
			</CustomPopup>
		</div>
	)
}

export default SlrBookingInputFields
